import { getCountryCurrency } from '../services/countryService';
import { usesMetricSystem } from './measurementSystemUtils';
import countryPreferences from './country_food_preferences.json';
import {
  MODEL_CONFIG,
  cleanJsonResponse
} from './../services/apiConfig';
import { fetchFromBackend } from './../services/aiProxyClient';

import { processGroceryList } from './groceryProcessor';
import { normalizeCategory, PRODUCT_METADATA } from './groceryConstants';
import { findProductMetadata } from './groceryUtils';

// Filter out unnecessary fields from unmatchedIngredients before sending to OpenAI
const cleanUnmatchedIngredients = (unmatchedIngredients) => {
  return unmatchedIngredients.map(({ name, totalQty, unit }) => ({
    name,
    totalQty,
    unit
  }));
};

// Category-based average pricing structure
const CATEGORY_PRICING = {
  'Meat & Seafood': { metric: { base: 8.0, perKg: true }, imperial: { base: 3.6, perLb: true } },
  'Produce': { metric: { base: 2.5, perKg: true }, imperial: { base: 1.1, perLb: true } },
  'Dairy': { metric: { base: 3.5, per250g: true }, imperial: { base: 4.0, per8oz: true } },
  'Alternatives & Eggs': { metric: { base: 4.0, per250g: true }, imperial: { base: 4.5, per8oz: true } },
  'Grains & Pasta': { metric: { base: 2.0, per500g: true }, imperial: { base: 2.5, per16oz: true } },
  'Condiments': { metric: { base: 3.0, per250ml: true }, imperial: { base: 3.5, per8oz: true } },
  'Pantry': { metric: { base: 2.5, per500g: true }, imperial: { base: 3.0, per16oz: true } },
  'Frozen': { metric: { base: 6.0, per500g: true }, imperial: { base: 4.0, per16oz: true } },
  'Bakery': { metric: { base: 3.0, per500g: true }, imperial: { base: 3.5, per16oz: true } },
  'Refrigerated': { metric: { base: 4.0, per250g: true }, imperial: { base: 4.5, per8oz: true } }
};

// Smallest suitable package sizes by category
const SMALLEST_PACKAGES = {
  metric: {
    'Meat & Seafood': [250, 500, 750, 1000], // grams
    'Produce': [500, 1000], // per kg pricing
    'Dairy': [125, 200, 250, 500], // grams/ml
    'Alternatives & Eggs': [200, 250, 500], // grams/ml
    'Grains & Pasta': [250, 500, 1000], // grams
    'Condiments': [125, 200, 250, 500], // ml
    'Pantry': [250, 500, 1000], // grams
    'Frozen': [250, 400, 500, 750], // grams
    'Bakery': [300, 400, 500, 800], // grams
    'Refrigerated': [200, 250, 400, 500] // grams/ml
  },
  imperial: {
    'Meat & Seafood': [8, 12, 16, 32], // oz
    'Produce': [16], // per lb pricing
    'Dairy': [4, 6, 8, 16], // oz/fl oz
    'Alternatives & Eggs': [6, 8, 16], // oz/fl oz
    'Grains & Pasta': [8, 16, 32], // oz
    'Condiments': [4, 6, 8, 16], // fl oz
    'Pantry': [8, 16, 32], // oz
    'Frozen': [8, 12, 16, 24], // oz
    'Bakery': [10, 12, 16, 24], // oz
    'Refrigerated': [6, 8, 12, 16] // oz/fl oz
  }
};

// Calculate category-based pricing with smallest package
const calculateCategoryPricing = (category, packageSize, isMetric, measurementUnit) => {
  const categoryData = CATEGORY_PRICING[category];
  if (!categoryData) return 2.50; // fallback price
  
  const pricing = isMetric ? categoryData.metric : categoryData.imperial;
  const basePrice = pricing.base;
  
  // For weight-based pricing (per kg/lb)
  if (pricing.perKg || pricing.perLb) {
    const weightInBaseUnit = isMetric ? packageSize / 1000 : packageSize / 16; // convert to kg or lb
    return parseFloat((basePrice * weightInBaseUnit).toFixed(2));
  }
  
  // For package-based pricing - scale based on package size relative to base size
  let baseSize = 250; // Default base size
  if (pricing.per250g || pricing.per8oz) baseSize = isMetric ? 250 : 8;
  if (pricing.per500g || pricing.per16oz) baseSize = isMetric ? 500 : 16;
  if (pricing.per250ml) baseSize = 250;
  
  const priceRatio = packageSize / baseSize;
  return parseFloat((basePrice * priceRatio).toFixed(2));
};

// Get smallest suitable package size for category
const getSmallestPackageSize = (category, isMetric) => {
  const packages = isMetric ? SMALLEST_PACKAGES.metric : SMALLEST_PACKAGES.imperial;
  const categoryPackages = packages[category] || packages['Pantry'];
  return categoryPackages[0]; // Return smallest size
};

// Get country-specific supermarket and brand information with abstract product names
const getCountrySpecificInfo = (country, isMetric) => {
  const countryLower = country.toLowerCase();
  
  // Try to find the country in the preferences JSON
  const countryKey = Object.keys(countryPreferences).find(key => 
    key.toLowerCase() === countryLower
  );
  
  if (countryKey && countryPreferences[countryKey]) {
    const countryData = countryPreferences[countryKey];
    
    // Check if this country has supermarket data
    if (countryData.supermarkets && countryData.packageExamples) {
      const supermarketList = countryData.supermarkets.join(", ");
      
      // Generate examples based on country's measurement system
      const actualIsMetric = countryData.measurementSystem === 'metric';
      const examples = generateCountryExamples(countryKey, actualIsMetric);
      
      return {
        supermarkets: `${countryKey} supermarkets like ${supermarketList}`,
        packageExamples: countryData.packageExamples.map(ex => `"${ex}"`),
        examples: examples
      };
    }
  }
  
  // Fallback for countries without specific data
  const defaultPackageExamples = isMetric ? 
    ['"Package 250 g"', '"Jar 500 ml"', '"Bottle 1 L"', '"Container 200 g"'] :
    ['"Package 8.8 oz"', '"Jar 16.9 fl oz"', '"Bottle 33.8 fl oz"', '"Container 7 oz"'];
    
  const defaultExamples = isMetric ? [
    '- Feta Cheese: type="package", productName="Feta Cheese", price=2.75, packageSize=200, measurementUnit="g", packageDescription="Package 200 g"',
    '- Heavy Cream: type="package", productName="Heavy Cream", price=2.80, packageSize=200, measurementUnit="ml", packageDescription="Carton 200 ml"',
    '- Parmesan: type="package", productName="Grated Parmesan", price=3.50, packageSize=100, measurementUnit="g", packageDescription="Container 100 g"',
    '- Broccoli: type="weight", productName="Fresh Broccoli", price=2.50, packageSize=1000, measurementUnit="g", packageDescription="Price per kg"'
  ] : [
    '- Feta Cheese: type="package", productName="Feta Cheese", price=3.20, packageSize=7, measurementUnit="oz", packageDescription="Package 7 oz"',
    '- Heavy Cream: type="package", productName="Heavy Cream", price=3.20, packageSize=8, measurementUnit="fl oz", packageDescription="Carton 8 fl oz"',
    '- Parmesan: type="package", productName="Grated Parmesan", price=2.40, packageSize=4, measurementUnit="oz", packageDescription="Container 4 oz"',
    '- Broccoli: type="weight", productName="Fresh Broccoli", price=1.76, packageSize=16, measurementUnit="oz", packageDescription="Price per lb"'
  ];

  return {
    supermarkets: `local supermarkets in ${country}`,
    packageExamples: defaultPackageExamples,
    examples: defaultExamples
  };
};

// Generate country-specific examples with abstract product names and category-based pricing
const generateCountryExamples = (countryName, isMetric) => {
  const countryData = countryPreferences[countryName];
  if (!countryData) return [];

  // Create abstract product examples with smallest suitable packages and category-based pricing
  if (countryName === 'Spain') {
    return isMetric ? [
      '- Feta Cheese: type="package", productName="Feta Cheese", price=2.75, packageSize=200, measurementUnit="g", packageDescription="Paquete 200 g"',
      '- Heavy Cream: type="package", productName="Heavy Cream", price=2.80, packageSize=200, measurementUnit="ml", packageDescription="Brick 200 ml"',
      '- Parmesan: type="package", productName="Grated Parmesan", price=3.50, packageSize=100, measurementUnit="g", packageDescription="Envase 100 g"',
      '- Broccoli: type="weight", productName="Fresh Broccoli", price=2.50, packageSize=1000, measurementUnit="g", packageDescription="€2.50/kg"'
    ] : [];
  } else if (countryName === 'USA') {
    return isMetric ? [] : [
      '- Feta Cheese: type="package", productName="Feta Cheese", price=3.20, packageSize=7, measurementUnit="oz", packageDescription="Package 7 oz"',
      '- Heavy Cream: type="package", productName="Heavy Cream", price=3.20, packageSize=8, measurementUnit="fl oz", packageDescription="Carton 8 fl oz"',
      '- Parmesan: type="package", productName="Grated Parmesan", price=2.40, packageSize=4, measurementUnit="oz", packageDescription="Container 4 oz"',
      '- Broccoli: type="weight", productName="Fresh Broccoli", price=1.76, packageSize=16, measurementUnit="oz", packageDescription="$1.76/lb"'
    ];
  } else if (countryName === 'Germany') {
    return isMetric ? [
      '- Feta Cheese: type="package", productName="Feta Cheese", price=2.75, packageSize=200, measurementUnit="g", packageDescription="Packung 200 g"',
      '- Heavy Cream: type="package", productName="Heavy Cream", price=2.80, packageSize=200, measurementUnit="ml", packageDescription="Becher 200 ml"',
      '- Parmesan: type="package", productName="Grated Parmesan", price=3.50, packageSize=100, measurementUnit="g", packageDescription="Dose 100 g"',
      '- Broccoli: type="weight", productName="Fresh Broccoli", price=2.50, packageSize=1000, measurementUnit="g", packageDescription="€2.50/kg"'
    ] : [];
  } else if (countryName === 'France') {
    return isMetric ? [
      '- Feta Cheese: type="package", productName="Feta Cheese", price=2.75, packageSize=200, measurementUnit="g", packageDescription="Paquet 200 g"',
      '- Heavy Cream: type="package", productName="Heavy Cream", price=2.80, packageSize=200, measurementUnit="ml", packageDescription="Pot 200 ml"',
      '- Parmesan: type="package", productName="Grated Parmesan", price=3.50, packageSize=100, measurementUnit="g", packageDescription="Paquet 100 g"',
      '- Broccoli: type="weight", productName="Fresh Broccoli", price=2.50, packageSize=1000, measurementUnit="g", packageDescription="€2.50/kg"'
    ] : [];
  } else if (countryName === 'United Kingdom') {
    return isMetric ? [
      '- Feta Cheese: type="package", productName="Feta Cheese", price=2.75, packageSize=200, measurementUnit="g", packageDescription="Pack 200g"',
      '- Heavy Cream: type="package", productName="Heavy Cream", price=2.80, packageSize=200, measurementUnit="ml", packageDescription="Pot 200ml"',
      '- Parmesan: type="package", productName="Grated Parmesan", price=3.50, packageSize=100, measurementUnit="g", packageDescription="Pack 100g"',
      '- Broccoli: type="weight", productName="Fresh Broccoli", price=2.50, packageSize=1000, measurementUnit="g", packageDescription="£2.50/kg"'
    ] : [];
  } else if (countryName === 'Italy') {
    return isMetric ? [
      '- Feta Cheese: type="package", productName="Feta Cheese", price=2.75, packageSize=200, measurementUnit="g", packageDescription="Confezione 200 g"',
      '- Heavy Cream: type="package", productName="Heavy Cream", price=2.80, packageSize=200, measurementUnit="ml", packageDescription="Brick 200 ml"',
      '- Parmesan: type="package", productName="Grated Parmesan", price=3.50, packageSize=100, measurementUnit="g", packageDescription="Confezione 100 g"',
      '- Broccoli: type="weight", productName="Fresh Broccoli", price=2.50, packageSize=1000, measurementUnit="g", packageDescription="€2.50/kg"'
    ] : [];
  } else if (countryName === 'Canada') {
    return isMetric ? [
      '- Feta Cheese: type="package", productName="Feta Cheese", price=2.75, packageSize=200, measurementUnit="g", packageDescription="Package 200g"',
      '- Heavy Cream: type="package", productName="Heavy Cream", price=2.80, packageSize=200, measurementUnit="ml", packageDescription="Carton 200ml"',
      '- Parmesan: type="package", productName="Grated Parmesan", price=3.50, packageSize=125, measurementUnit="g", packageDescription="Container 125g"',
      '- Broccoli: type="weight", productName="Fresh Broccoli", price=2.50, packageSize=1000, measurementUnit="g", packageDescription="C$2.50/kg"'
    ] : [];
  }
  
  // Default examples for other countries with abstract names
  return isMetric ? [
    '- Feta Cheese: type="package", productName="Feta Cheese", price=2.75, packageSize=200, measurementUnit="g", packageDescription="Package 200 g"',
    '- Heavy Cream: type="package", productName="Heavy Cream", price=2.80, packageSize=200, measurementUnit="ml", packageDescription="Carton 200 ml"',
    '- Parmesan: type="package", productName="Grated Parmesan", price=3.50, packageSize=100, measurementUnit="g", packageDescription="Container 100 g"',
    '- Broccoli: type="weight", productName="Fresh Broccoli", price=2.50, packageSize=1000, measurementUnit="g", packageDescription="€2.50/kg"'
  ] : [
    '- Feta Cheese: type="package", productName="Feta Cheese", price=3.20, packageSize=7, measurementUnit="oz", packageDescription="Package 7 oz"',
    '- Heavy Cream: type="package", productName="Heavy Cream", price=3.20, packageSize=8, measurementUnit="fl oz", packageDescription="Carton 8 fl oz"',
    '- Parmesan: type="package", productName="Grated Parmesan", price=2.40, packageSize=4, measurementUnit="oz", packageDescription="Container 4 oz"',
    '- Broccoli: type="weight", productName="Fresh Broccoli", price=1.76, packageSize=16, measurementUnit="oz", packageDescription="$1.76/lb"'
  ];
};

/**
 * Uses OpenAI to fill in grocery data for unmatched ingredients
 * Returns products in the same structure as matched products so they can go through quantity calculations
 */
export const processUnmatchedIngredientsWithAI = async (unmatchedIngredients, country) => {
  if (!unmatchedIngredients || unmatchedIngredients.length === 0) return [];
  
  try {
    const isMetric = usesMetricSystem(country);
    const unitSystem = isMetric ? "metric (g, ml)" : "imperial (oz, fl oz)";
    const currencyInfo = getCountryCurrency(country);

    // Clean the unmatchedIngredients array
    const cleanedIngredients = cleanUnmatchedIngredients(unmatchedIngredients);
    
    // Get country-specific information
    const countrySpecific = getCountrySpecificInfo(country, isMetric);

    // Create measurement-specific guidelines
    const measurementGuidelines = isMetric ? {
      packageSizeUnit: 'grams/ml',
      weightUnit: 'g',
      volumeUnit: 'ml',
      weightPricing: 'per kg',
      packageSizes: {
        dairy: '125-250g/ml packages',
        meat: '250-500g packages',
        condiments: '125-250ml bottles',
        pantry: '250-500g packages',
        frozen: '250-500g packages',
        bakery: '300-500g packages',
        refrigerated: '200-400g/ml packages'
      }
    } : {
      packageSizeUnit: 'ounces/fl oz',
      weightUnit: 'oz',
      volumeUnit: 'fl oz',
      weightPricing: 'per lb',
      packageSizes: {
        dairy: '4-8oz/fl oz packages',
        meat: '8-16oz packages',
        condiments: '4-8fl oz bottles',
        pantry: '8-16oz packages',
        frozen: '8-16oz packages',
        bakery: '10-16oz packages',
        refrigerated: '6-12oz/fl oz packages'
      }
    };
// - if ${country} is Estonia, then productName has to be in Russian
// - if ${country} is Estonia, then packageDescription has to be in Russian
    const userPrompt = `
You are a grocery product expert for ${country}. For each ingredient, provide realistic product information that matches how products are sold in ${countrySpecific.supermarkets}.

**CRITICAL: You are ONLY defining products, NOT calculating purchase quantities or totals. All quantity/price calculations will be done later.**

**IMPORTANT: Use GENERIC product names without brand names. Focus on simple, descriptive product names.**

For each ingredient, provide:
- name: ingredient name (must match exactly)
- productName: product name without brands (e.g., "Fresh Chicken Breast", "Extra Virgin Olive Oil", "Whole Milk") - if ${country} is Estonia, then productName has to be in Russian
- category: one of: Meat & Seafood, Produce, Dairy, Alternatives & Eggs, Grains & Pasta, Condiments, Pantry, Frozen, Bakery, Refrigerated
- type: "package", "weight", or "unit" (how it's typically sold)
- price: calculated using category-appropriate pricing for smallest suitable package
- packageSize: smallest suitable package size for the category in ${measurementGuidelines.packageSizeUnit}
- measurementUnit: "${measurementGuidelines.weightUnit}" or "piece" or "${measurementGuidelines.volumeUnit}" for liquids
- packageDescription: realistic package description (e.g., ${countrySpecific.packageExamples.join(', ')}) // - if ${country} is Estonia, then packageDescription has to be in Russian

**IMPORTANT FOR WEIGHT-BASED PRODUCTS (type="weight"):**
- For packageDescription, use actual price per unit format with currency symbol
- Use "${currencyInfo.symbol}X.XX/${isMetric ? 'kg' : 'lb'}" format (e.g., "${currencyInfo.symbol}2.50/${isMetric ? 'kg' : 'lb'}")
- DO NOT use generic descriptions like "Price per ${isMetric ? 'kg' : 'lb'}"

**Examples for ${country} (note generic product names):**
${countrySpecific.examples.join('\n')}

**Product Type Guidelines:**
- "package": Pre-packaged items with fixed sizes (cheese blocks, yogurt containers, etc.) - use smallest suitable package
- "weight": Fresh produce sold by weight (vegetables, fruits, meat, etc.) - price ${measurementGuidelines.weightPricing} with actual price format
- "unit": Individual items sold by piece (eggs, avocados, etc.)

**Package Size Guidelines (${unitSystem}):**
- Choose the SMALLEST reasonable package size for each category
- Dairy: ${measurementGuidelines.packageSizes.dairy} preferred
- Meat: ${measurementGuidelines.packageSizes.meat} preferred  
- Condiments: ${measurementGuidelines.packageSizes.condiments} preferred
- Produce: Always ${measurementGuidelines.weightPricing} pricing with actual price format (${currencyInfo.symbol}X.XX/${isMetric ? 'kg' : 'lb'})
- Pantry items: ${measurementGuidelines.packageSizes.pantry} preferred
- Frozen: ${measurementGuidelines.packageSizes.frozen} preferred (frozen vegetables, fish fillets, etc.)
- Bakery: ${measurementGuidelines.packageSizes.bakery} preferred (bread loaves, croissants, etc.)
- Refrigerated: ${measurementGuidelines.packageSizes.refrigerated} preferred (pastries, fresh pasta, etc.)

Ingredients: ${JSON.stringify(cleanedIngredients, null, 2)}

Currency: ${currencyInfo.symbol}
System: ${unitSystem}

**Important: Return realistic generic ${country} products with smallest suitable packages for ${isMetric ? 'metric' : 'imperial'} measurements. NO BRAND NAMES.**
**Important: For weight-based products, use actual price format: ${currencyInfo.symbol}X.XX/${isMetric ? 'kg' : 'lb'} in packageDescription.**
**Important: If the ${country} is Estonia, then packageDescription has to be in Russian.**
Return ONLY valid JSON array. No markdown.`;

console.log("📝 AI grocery prompt:", userPrompt);

    // Use backend proxy for grocery fallback
    const systemPrompt = `You are a grocery expert for ${country}. Return only JSON arrays with realistic ${country} supermarket product data including proper package descriptions using ${isMetric ? 'metric units (g, ml, kg, L)' : 'imperial units (oz, fl oz, lb)'} and ${currencyInfo.symbol} currency.`;
    const result = await fetchFromBackend('/grocery-fallback', {
        model: MODEL_CONFIG.groceryFallback,
        systemPrompt,
        userPrompt,
      });

      const rawText = result.text;
      let cleaned = cleanJsonResponse(rawText);
      
      // Ensure array format
      if (cleaned && cleaned.trim() && !cleaned.trim().startsWith('[')) {
        cleaned = '[' + cleaned + ']';
      }

      let aiProducts;
      try {
        aiProducts = JSON.parse(cleaned);
        if (!Array.isArray(aiProducts)) {
          aiProducts = [aiProducts];
        }
      } catch (jsonErr) {
        console.error("❌ AI JSON parse error:", cleaned);
        return [];
      }

      // Transform AI response to match database product structure with validation and enhancements
      const processedProducts = aiProducts.map(item => {
        // Find the original unmatched ingredient to preserve its data
        const originalIngredient = unmatchedIngredients.find(ing => 
          ing.name.toLowerCase() === item.name.toLowerCase()
        );

        if (!originalIngredient) {
          console.warn(`⚠️ AI product ${item.name} doesn't match any unmatched ingredient`);
          return null;
        }

        // Ensure category-based pricing and smallest package size
        const category = normalizeCategory(item.category);
        const packageSize = item.packageSize || getSmallestPackageSize(category, isMetric);
        
        // Robust price validation - fallback if price is missing, 0, or invalid
        let price = parseFloat(item.price);
        if (!price || price <= 0 || isNaN(price)) {
          price = calculateCategoryPricing(category, packageSize, isMetric, item.measurementUnit);
        }
        
        // FIX: Validate and correct product type inconsistencies
        let productType = item.type || "package";
        let measurementUnit = item.measurementUnit || (isMetric ? "g" : "oz");
        
        // Look up product metadata using the imported function
        const productMetadata = findProductMetadata(item.name);
        let avgWeightValue = productMetadata?.avgWeight?.[isMetric ? 'metric' : 'imperial'] || null;
        
        // CRITICAL FIX: Detect and fix banana-type issues
        if (productType === 'weight' && (measurementUnit === 'piece' || measurementUnit === 'unit')) {
          console.warn(`🔧 Fixing invalid product type for ${item.name}: weight product cannot have unit-based weightUnit`);
          productType = 'unit';
          measurementUnit = 'piece';
          
          // Set avgWeight for unit products if available from metadata
          if (!avgWeightValue && productMetadata?.avgWeight) {
            avgWeightValue = productMetadata.avgWeight[isMetric ? 'metric' : 'imperial'];
          }
          
          // Fallback avgWeight for common produce items
          if (!avgWeightValue) {
            const commonWeights = {
              metric: { banana: 120, apple: 150, orange: 150, onion: 150, tomato: 150 },
              imperial: { banana: 4.2, apple: 5.3, orange: 5.3, onion: 5.3, tomato: 5.3 }
            };
            const system = isMetric ? 'metric' : 'imperial';
            const itemName = item.name.toLowerCase();
            
            for (const [key, weight] of Object.entries(commonWeights[system])) {
              if (itemName.includes(key)) {
                avgWeightValue = weight;
                break;
              }
            }
          }
        }

        // Create product structure identical to database products
        const productId = `ai_${item.name.toLowerCase().replace(/\s+/g, '_')}`;
        const mockProduct = {
          id: productId,
          name: item.productName || item.name,
          category: category,
          sizes: [{
            name: item.productName || item.name,
            image: "https://via.placeholder.com/300x300.png?text=AI+Product",
            type: productType, // Use corrected type
            price: parseFloat(price),
            weight: productType === 'weight' ? parseFloat(packageSize) : (productType === 'unit' ? 1 : parseFloat(packageSize)),
            weightUnit: productType === 'weight' ? measurementUnit : (productType === 'unit' ? 'piece' : measurementUnit),
            avgWeight: avgWeightValue, // Use corrected avgWeight
            itemsQty: productType === 'unit' ? 1 : (productType === 'package' ? parseFloat(packageSize) : null),
            description: item.packageDescription || `${item.productName || item.name} - AI Generated`,
            rating: 3.0,
            segment: "standard"
          }]
        };

        // Return in EXACT same structure as matchIngredientsToProductsMetric
        return {
          name: originalIngredient.name,
          originalName: originalIngredient.name,
          allOriginals: [originalIngredient.name],
          totalQty: originalIngredient.totalQty,
          unit: originalIngredient.unit,
          // Preserve the original measurement type - it was correctly determined during normalization
          // The measurement type should be based on the ingredient's unit, not the AI's product type
          measurementType: originalIngredient.measurementType || 'weight',
          originalUnit: originalIngredient.unit,
          // Smart recalculation tracking
          source: 'ai', // Track that this came from AI
          originalIngredientNames: [originalIngredient.name], // Track which ingredient names this covers
          usedIn: [],
          productId: productId,
          productName: mockProduct.name,
          category: category,
          perishable: productMetadata?.perishable || false,
          shelfLifeDays: productMetadata?.shelfLifeDays || 7,
          unmatched: false, // Now matched by AI
          matchConfidence: 0.8,
          product: mockProduct, // Full product for three-parameter evaluation
          ai_data: true
          // NO totalPrice - will be calculated by calculatePurchaseQuantitiesMetric/Imperial
        };
      }).filter(Boolean); // Remove any null entries

      return processedProducts;

    return [];
  } catch (error) {
    console.error("Error in AI grocery fallback:", error);
    return [];
  }
};

/**
 * Your orchestrated AI-aware grocery processor entry point
 */
export const processGroceryListAI = async ({
  mealPlan,
  country,
  numberOfPeople,
  budget = null, // Add budget parameter
  prematchCache = null,
  onProgress = null
}) => {
  try {
    const result = await processGroceryList(mealPlan, country, numberOfPeople, budget, (progress) => {
      if (onProgress) onProgress(progress);
    }, { prematchCache });
    return result;
  } catch (error) {
    console.error('Error in grocery processing with AI fallback:', error);
    if (onProgress) {
      onProgress({ step: 'error', progress: 0, message: `Error: ${error.message}` });
    }
    return { error: error.message };
  }
};